import * as Api from '../../../weborder/Api';
import * as BasketControl from '../../../weborder/BasketControl';

const calculateItemCounts = (basket) => {
  const counts: any = {};

  for (let i = 0; i < basket.items.length; i++) {
    const item = basket.items[i];
    const key = item.Item.Name + item.Options.map((o) => o.Name).join(', ');
    if (counts[key]) {
      counts[key].occurence = counts[key].occurence
        ? counts[key].occurence + 1
        : 1;
      counts[key].index = i;
      counts[key].item = item;
    } else {
      counts[key] = { occurence: 1, index: i, item };
    }
  }

  return counts;
};

const computeBasketTaxes = (basket): number => {
  let basketTaxes = 0;

  for (const item of basket.items) {
    basketTaxes += item.Item.TaxValue;

    for (const option of item.Options) {
      basketTaxes += option.TaxValue;
    }
  }

  return basketTaxes;
};

const computeDeliveryFee = (restaurant): number => {
  if (!restaurant) {
    return 0;
  }

  switch (restaurant.DeliveryType) {
    case Api.DeliveryType.Delivery:
      return restaurant.DeliveryFee;

    default:
      return 0;
  }
};

const computePurchaseFee = (
  account,
  basket,
  pickupLocationType,
  restaurant,
) => {
  if (account) {
    const paymentAccountIsCashFee =
      account.PaymentAccountType === 'CashPaymentAccount' ||
      account.PaymentAccountType === Api.GatewayPaymentAccount.Ideal ||
      account.PaymentAccountType === Api.GatewayPaymentAccount.Bancontact;

    /*
     * PaymentOptionType is when you click on New Card or New Paypal account
     * There is no PaymentAccount associated with these options but
     * They still should have a card fee added to totalToPay as payment goes through.
     */

    const shouldUseCashFee = paymentAccountIsCashFee;

    if (!restaurant) {
      return 0;
    }

    const isTableService =
      restaurant.DeliveryType === Api.DeliveryType.Collection &&
      restaurant.TableServiceCatagory ===
        (Api.TableServiceCategory.Generic || Api.TableServiceCategory.Table);

    const isTakeOutSelected = pickupLocationType === 0;

    if (shouldUseCashFee) {
      if (isTableService && !isTakeOutSelected) {
        return BasketControl.TableCashFees(
          BasketControl.Total(basket.items),
          restaurant,
        );
      } else {
        return BasketControl.CashFees(
          BasketControl.Total(basket.items),
          restaurant,
        );
      }
    } else {
      if (isTableService && !isTakeOutSelected) {
        return BasketControl.TableCardFees(
          BasketControl.Total(basket.items),
          restaurant,
        );
      } else {
        return BasketControl.CardFees(
          BasketControl.Total(basket.items),
          restaurant,
        );
      }
    }
  } else {
    return 0;
  }
};

const computeTaxes = (basket, restaurant): number => {
  const basketTaxes = computeBasketTaxes(basket);
  return (restaurant.DeliveryFeeTaxAmount || 0) + basketTaxes;
};

const getChefNote = (chefNote, chefNotes, restaurant): string => {
  switch (chefNote.type) {
    case 'custom':
      return chefNotes;
    case 'loaded':
      if (chefNotes) {
        return chefNotes;
      }
      break;
  }

  return (restaurant && restaurant.ChefNote) || '';
};

const getErrorMessage = (paymentError: string): string => {
  switch (paymentError) {
    case 'AuthenticationFailure':
      return 'Card_verification_failed_Please_try_again';
    case 'PaymentFailure':
    case 'ProcessingError':
      return 'Payment_failed_Please_try_again';
    case 'InsufficientFunds':
      return 'Your_card_has_insufficient_funds';
    case 'CardDeclined':
      return 'Card_declined_Please_use_another_card';
    case 'ExpiredCard':
      return 'Card_expired_Please_use_another_card';
    case 'IncorrectCvc':
      return 'Incorrect_cvc_Please_add_payment_method_again';
    default:
      return 'Incorrect_card_details_Please_try_again';
  }
};

const getItemTotalPrices = (basketItems) => {
  const basketItemPrices = Object.values(basketItems).map((item: any) =>
    BasketControl.ItemTotal(item.item),
  );
  return basketItemPrices;
};

const getOrder = (basket, orderPayload, orderTotalToPay, restaurant) => {
  const currency = (restaurant && restaurant.Currency) || Api.Currency.EUR;

  if (!orderPayload || typeof orderPayload === 'string') {
    return;
  }

  const v = basket.voucher;
  const voucher = v.status === 'valid' ? v.code : '';

  return {
    currency,
    order: orderPayload,
    totalToPay: orderTotalToPay,
    voucher,
  };
};

const getRestaurantUrl = (params): string => {
  const { concessionStoreId, id, type, vrid } = params;
  const concessionId = concessionStoreId ? '/' + concessionStoreId : '';
  const restaurant = `/restaurant/${vrid}/${type}/${id}${concessionId}`;
  return restaurant;
};

const isTipablePaymentMethod = (account): boolean =>
  account && account.SupportsSubmitOrderTips;

const isTipEnabled = (appConfig, customer, restaurant): boolean => {
  if (
    appConfig.status !== 'success' ||
    customer.status !== 'success' ||
    !restaurant
  ) {
    return false;
  }

  return (
    appConfig.data.TipsEnabled &&
    customer.data.DisplayTipOption &&
    restaurant.TipsEnabled
  );
};

export const checkoutUtils = {
  calculateItemCounts,
  computeBasketTaxes,
  computeDeliveryFee,
  computePurchaseFee,
  computeTaxes,
  getChefNote,
  getErrorMessage,
  getItemTotalPrices,
  getOrder,
  getRestaurantUrl,
  isTipablePaymentMethod,
  isTipEnabled,
};
